import type { ApiLeagueCreatePayload } from '@/api/leagues';

const STORAGE_KEY = 'pendingLeagueCreation';
const POST_LOGIN_ACTION_KEY = 'postLoginAction';
const MAX_AGE_MS = 30 * 60 * 1000;

export interface PendingLeagueCreation {
  version: 1;
  savedAt: number;
  idempotencyKey: string;
  payload: ApiLeagueCreatePayload;
}

const isPositiveInteger = (value: unknown): value is number =>
  Number.isInteger(value) && Number(value) > 0;

const isLeaguePayload = (value: unknown): value is ApiLeagueCreatePayload => {
  if (!value || typeof value !== 'object') return false;
  const payload = value as Partial<ApiLeagueCreatePayload>;
  const name = typeof payload.name === 'string' ? payload.name.trim() : '';
  if (name.length < 3 || name.length > 70) return false;
  if (payload.name !== name) return false;
  if (!isPositiveInteger(payload.competition_id)) return false;
  if (!Number.isInteger(payload.max_teams) || payload.max_teams! < 4 || payload.max_teams! > 16 || payload.max_teams! % 2 !== 0) return false;
  if (payload.draft_type !== 'OFFLINE' && payload.draft_type !== 'SNAKE') return false;
  if (payload.season_type !== 'FALL' && payload.season_type !== 'FULL') return false;
  if (![2, 4, 6, 8].includes(payload.num_playoff_teams ?? 0)) return false;
  if ((payload.num_playoff_teams ?? 0) >= payload.max_teams!) return false;
  if (payload.additional_competition_ids !== undefined) {
    if (!Array.isArray(payload.additional_competition_ids)) return false;
    if (!payload.additional_competition_ids.every(isPositiveInteger)) return false;
    if (payload.additional_competition_ids.includes(payload.competition_id!)) return false;
    if (new Set(payload.additional_competition_ids).size !== payload.additional_competition_ids.length) return false;
  }
  return true;
};

const generateUuid = (): string => {
  if (typeof globalThis.crypto.randomUUID === 'function') return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  return [...bytes].map((byte, index) => {
    const hex = byte.toString(16).padStart(2, '0');
    return [4, 6, 8, 10].includes(index) ? `-${hex}` : hex;
  }).join('');
};

const canonicalPayload = (payload: ApiLeagueCreatePayload): string => JSON.stringify({
  name: payload.name,
  competition_id: payload.competition_id,
  max_teams: payload.max_teams,
  draft_type: payload.draft_type,
  season_type: payload.season_type,
  num_playoff_teams: payload.num_playoff_teams,
  additional_competition_ids: [...(payload.additional_competition_ids ?? [])].sort((a, b) => a - b),
});

export const newPendingLeagueCreation = (payload: ApiLeagueCreatePayload): PendingLeagueCreation => ({
  version: 1,
  savedAt: Date.now(),
  idempotencyKey: generateUuid(),
  payload,
});

export const pendingLeagueCreationMatches = (
  pending: PendingLeagueCreation,
  payload: ApiLeagueCreatePayload,
): boolean => canonicalPayload(pending.payload) === canonicalPayload(payload);

export const savePendingLeagueCreation = (pending: PendingLeagueCreation): boolean => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
    return true;
  } catch {
    return false;
  }
};

export const loadPendingLeagueCreation = (): PendingLeagueCreation | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as Partial<PendingLeagueCreation>;
    const age = typeof pending.savedAt === 'number' ? Date.now() - pending.savedAt : Number.POSITIVE_INFINITY;
    const isCurrent = age >= 0 && age <= MAX_AGE_MS;
    const isValid = pending.version === 1
      && isCurrent
      && typeof pending.idempotencyKey === 'string'
      && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(pending.idempotencyKey)
      && isLeaguePayload(pending.payload);
    if (!isValid) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return pending as PendingLeagueCreation;
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* storage unavailable */ }
    return null;
  }
};

export const clearPendingLeagueCreation = (): void => {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* storage unavailable */ }
};

export const markLeagueCreationForPostLogin = (): void => {
  try {
    // The existing DFS stash/resume flow owns this single action slot. The
    // newest persistent action wins, preventing an abandoned DFS marker from
    // hijacking this login before the intended league redirect is consumed.
    // completeLogin never acts on this value; it exists only to displace a
    // stale submitDfsLineup marker. Since the URL intent contract
    // (utils/authIntent, CREATE-LEAGUE-FUNNEL.md §3.4) the intent branch in
    // completeLogin also bypasses the slot outright, making this the second
    // of two independent guards - keep both.
    sessionStorage.setItem(POST_LOGIN_ACTION_KEY, JSON.stringify({ action: 'createSeasonLongLeague' }));
  } catch { /* the URL intent + intendedRedirectPath remain the fallbacks */ }
};

export const clearLeagueCreationPostLoginMarker = (): void => {
  try {
    const raw = sessionStorage.getItem(POST_LOGIN_ACTION_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as { action?: unknown };
    if (parsed.action === 'createSeasonLongLeague') {
      sessionStorage.removeItem(POST_LOGIN_ACTION_KEY);
    }
  } catch {
    // A corrupt marker would break completeLogin's JSON parse on a later auth.
    try { sessionStorage.removeItem(POST_LOGIN_ACTION_KEY); } catch { /* unavailable */ }
  }
};
